/**
 * The progression screens' data, as plain values.
 *
 * `MenuModel` mirrors a `ProgressionState` exactly as it mirrors a `NetView` —
 * see the header of ./net-model. The model never loads it, never saves it and
 * never reads a clock to build it; `ProgressionRuntime` in ./progression owns
 * all three, and hands a snapshot over. That is what keeps the menu model
 * constructible in Node with no storage and no wall clock, and it is what lets
 * the shop, pass and challenge screens be tested on a Wednesday in 2031.
 *
 * Everything here is a pure function of that snapshot. There is no DOM, no
 * `Date.now`, and no `Math.random`.
 */

import {
  type ChallengeDef,
  type ChallengeProgress,
  type ChallengeReward,
  type PaintColor,
  type PlayerProfile,
  type Rarity,
  type ShopEntry,
  type ShopRotation,
} from '../game/cosmetics';
import { CHALLENGE_TIER_POINTS, TIER_POINTS_PER_TIER } from '../game/cosmetics';
import { SEASON_CHALLENGES, challengeById } from '../game/challenges';
import { itemById } from '../game/items';
import { purchaseProblem, type PurchaseFailure } from '../game/shop';
import {
  isTierClaimed,
  proTierStep,
  tierRewards,
  type PassClaims,
  type PassReward,
  type PassTrack,
} from '../game/rocketpass';

// ---------------------------------------------------------------------------
// The mirror
// ---------------------------------------------------------------------------

/**
 * Everything the three screens read.
 *
 * `now` is carried rather than fetched: every expiry test on these screens has
 * to agree with every other one within a single render, and a screen that calls
 * a clock twice can show a challenge as live in its list and expired on its
 * row.
 */
export interface ProgressionState {
  profile: PlayerProfile;
  claims: PassClaims;
  /** Epoch ms the runtime last refreshed at. Never read from a clock here. */
  now: number;
  shop: ShopRotation;
  /** When the shop rotation is replaced, epoch ms. */
  shopRefreshAt: number;
  /** When this week's challenges expire, epoch ms. */
  weekEndsAt: number;
  /** Highest open season stage, 1..SEASON_STAGES. */
  seasonStage: number;
}

// ---------------------------------------------------------------------------
// Shop rows
// ---------------------------------------------------------------------------

export interface ShopRow {
  entry: ShopEntry;
  /** 'featured' or 'daily' — which of the two rotations it came from. */
  row: 'featured' | 'daily';
  index: number;
  name: string;
  rarity: Rarity;
  /** Why it cannot be bought right now, or null. */
  problem: PurchaseFailure | null;
}

const paintLabel = (paint: PaintColor): string =>
  paint.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());

/** "Titanium White Cyclone" — the name a player would call the thing. */
export const entryName = (entry: ShopEntry): string => {
  const def = itemById(entry.itemId);
  const base = def?.name ?? entry.itemId;
  return entry.paint === undefined ? base : `${paintLabel(entry.paint)} ${base}`;
};

export const shopRows = (state: ProgressionState): ShopRow[] => {
  const build = (entries: readonly ShopEntry[], row: 'featured' | 'daily'): ShopRow[] =>
    entries.map((entry, index) => ({
      entry,
      row,
      index,
      name: entryName(entry),
      rarity: itemById(entry.itemId)?.rarity ?? 'common',
      problem: purchaseProblem(state.profile, entry),
    }));
  return [...build(state.shop.featured, 'featured'), ...build(state.shop.daily, 'daily')];
};

/** The note under a shop row: the price, the discount, or why it is refused. */
export const shopRowNote = (row: ShopRow): string => {
  switch (row.problem) {
    case 'owned':
      return 'Already in your inventory.';
    case 'insufficient-credits':
      return `${row.entry.price} Credits — you do not have enough yet. Credits are earned by playing.`;
    case 'unknown-item':
      return 'This item is no longer in the catalogue.';
    default:
      return row.entry.wasPrice === undefined
        ? `${row.entry.price} Credits.`
        : `${row.entry.price} Credits, down from ${row.entry.wasPrice}.`;
  }
};

// ---------------------------------------------------------------------------
// Pass rows
// ---------------------------------------------------------------------------

export interface PassRowTrack {
  reward: PassReward | null;
  claimed: boolean;
  /** True when it is reached, present, allowed and unclaimed. */
  claimable: boolean;
}

export interface PassRow {
  tier: number;
  /** Pro Tier step, 1-based, or null for one of the seventy. */
  pro: number | null;
  reached: boolean;
  free: PassRowTrack;
  premium: PassRowTrack;
}

export const passRow = (state: ProgressionState, tier: number): PassRow => {
  const rewards = tierRewards(tier);
  const reached = tier <= state.profile.progress.passTier;
  const track = (reward: PassReward | null, name: PassTrack): PassRowTrack => {
    const claimed = isTierClaimed(state.claims, tier, name);
    const allowed = name === 'free' || state.profile.progress.passPremium;
    return { reward, claimed, claimable: reward !== null && reached && allowed && !claimed };
  };
  return {
    tier,
    pro: proTierStep(tier),
    reached,
    free: track(rewards.free, 'free'),
    premium: track(rewards.premium, 'premium'),
  };
};

/**
 * A window of tiers centred on `tier`.
 *
 * A window rather than the whole seventy because the pass keeps going past
 * them: Pro Tiers are unbounded, so "render every tier" has no end, and a track
 * of seventy rows would not fit on the screen in any case.
 */
export const passWindow = (state: ProgressionState, tier: number, count: number): PassRow[] => {
  const half = Math.floor(count / 2);
  const first = Math.max(1, tier - half);
  return Array.from({ length: count }, (_, i) => passRow(state, first + i));
};

/** "150 Credits", "Dieci Wheels", "Titanium White Cyclone". */
export const passRewardLabel = (reward: PassReward | null): string => {
  if (reward === null) return '—';
  switch (reward.kind) {
    case 'credits':
      return `${reward.amount} Credits`;
    case 'xp':
      return `${reward.amount} XP`;
    case 'item': {
      const name = itemById(reward.itemId)?.name ?? reward.itemId;
      return reward.paint === undefined ? name : `${paintLabel(reward.paint)} ${name}`;
    }
  }
};

/** "Tier 12" or "Pro Tier 3". */
export const tierLabel = (tier: number): string => {
  const pro = proTierStep(tier);
  return pro === null ? `Tier ${tier}` : `Pro Tier ${pro}`;
};

// ---------------------------------------------------------------------------
// Challenge rows
// ---------------------------------------------------------------------------

export interface ChallengeRow {
  def: ChallengeDef;
  entry: ChallengeProgress;
  complete: boolean;
  claimed: boolean;
  expired: boolean;
  /** Tier Points it pays: `CHALLENGE_TIER_POINTS` for its difficulty. */
  tierPoints: number;
  /** 1..SEASON_STAGES for a season challenge, otherwise null. */
  stage: number | null;
}

/** Weeklies first, then the season ladder, then events. */
const KIND_ORDER = { weekly: 0, season: 1, event: 2 } as const;

/** Which season stage an id belongs to, or null when it is not a season one. */
export const seasonStageOf = (id: string): number | null =>
  SEASON_CHALLENGES.find(s => s.def.id === id)?.stage ?? null;

export const challengeRows = (state: ProgressionState): ChallengeRow[] => {
  const rows: ChallengeRow[] = [];
  for (const entry of state.profile.challenges) {
    const def = challengeById(entry.id);
    if (def === undefined) continue;
    rows.push({
      def,
      entry,
      complete: entry.value >= def.target,
      claimed: entry.claimed,
      expired: entry.expiresAt <= state.now,
      tierPoints: CHALLENGE_TIER_POINTS[def.difficulty],
      stage: seasonStageOf(entry.id),
    });
  }
  rows.sort((a, b) => {
    const kind = KIND_ORDER[a.def.kind] - KIND_ORDER[b.def.kind];
    if (kind !== 0) return kind;
    const stage = (a.stage ?? 0) - (b.stage ?? 0);
    if (stage !== 0) return stage;
    // Premium after free inside a kind, then by id so the order is stable.
    const paid = Number(a.def.premiumOnly ?? false) - Number(b.def.premiumOnly ?? false);
    return paid !== 0 ? paid : a.def.id.localeCompare(b.def.id);
  });
  return rows;
};

export const rewardLabel = (reward: ChallengeReward): string => {
  switch (reward.kind) {
    case 'xp':
      return `${reward.amount} XP`;
    case 'credits':
      return `${reward.amount} Credits`;
    case 'item':
      return itemById(reward.itemId)?.name ?? reward.itemId;
    case 'drop':
      return `${rarityLabel(reward.rarity)} drop`;
  }
};

/** "3 / 10". Rounded, because distance is a float and "3.7 / 10" is noise. */
export const progressLabel = (row: ChallengeRow): string =>
  `${Math.floor(Math.min(row.entry.value, row.def.target))} / ${row.def.target}`;

export const challengeRowNote = (row: ChallengeRow): string => {
  const reward = `${rewardLabel(row.def.reward)} · ${row.tierPoints} Tier Points`;
  if (row.claimed) return `${row.def.description}  ·  Claimed.`;
  if (row.expired) return `${row.def.description}  ·  Expired.`;
  if (row.complete) return `${row.def.description}  ·  Ready to claim: ${reward}.`;
  return `${row.def.description}  ·  ${reward}.`;
};

// ---------------------------------------------------------------------------
// Shared labels
// ---------------------------------------------------------------------------

const RARITY_LABELS: Readonly<Record<Rarity, string>> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  veryRare: 'Very Rare',
  import: 'Import',
  exotic: 'Exotic',
  blackMarket: 'Black Market',
  limited: 'Limited',
};

export const rarityLabel = (rarity: Rarity): string => RARITY_LABELS[rarity] ?? rarity;

/**
 * "2d 14h", "14h 05m", "05m". For a refresh or an expiry countdown.
 *
 * Takes the two instants rather than a duration so a caller cannot accidentally
 * pass a negative one it computed from a stale clock; anything already past
 * reads "now", which is what a screen should say while it waits for a refresh.
 */
export const untilLabel = (now: number, at: number): string => {
  const ms = at - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'now';
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d ${hours.toString().padStart(2, '0')}h`;
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  return `${Math.max(1, mins)}m`;
};

/** 0..1 through the current tier, from the mirrored progress. */
export const tierBarFraction = (state: ProgressionState): number =>
  Math.min(1, Math.max(0, state.profile.progress.tierPoints / TIER_POINTS_PER_TIER));

// ---------------------------------------------------------------------------
// The post-match summary
// ---------------------------------------------------------------------------

/**
 * What one match did to the profile, for the full-time screen.
 *
 * Built by the runtime at the moment the award lands, because most of it is a
 * DIFFERENCE and a difference cannot be recovered from the profile afterwards.
 */
export interface ProgressionSummary {
  xpGained: number;
  levelsGained: number;
  level: number;
  creditsGained: number;
  tierPointsGained: number;
  tiersGained: number;
  passTier: number;
  /** Names of the challenges this match completed. */
  challengesCompleted: string[];
  /** Items that dropped on a level-up. */
  drops: string[];
}
